import { Suspense, lazy, useEffect, useRef, useState } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { DemoPageHero, DemoStage } from '@/components/editorial/DemoPageLayout'
import SectionHeader from '@/components/editorial/SectionHeader'
import ApplicationEnquiry from '@/components/editorial/ApplicationEnquiry'
import DeferredSchemaFormUsage from '@/components/playground/DeferredSchemaFormUsage'
import Reveal from '@/components/preview/Reveal'

const SchemaBuilder = lazy(() =>
  import('@/components/playground/SchemaBuilder').then((module) => ({
    default: module.SchemaBuilder,
  })),
)

export const Route = createFileRoute('/playground/schemaform')({
  component: SchemaFormPage,
  head: () => {
    const url = 'https://www.suriyaprakash.in/playground/schemaform'
    const title = 'React Schema-Driven Form Builder Demo | Suriyaprakash Somu'
    const description =
      'Explore a schema-driven React form builder with configurable fields, layout, live preview and Zod schema output for consistent business application forms.'

    return {
      meta: [
        { title },
        { name: 'title', content: title },
        { name: 'description', content: description },
        { name: 'robots', content: 'index, follow' },
        { name: 'author', content: 'Suriyaprakash Somu' },
        { name: 'geo.region', content: 'IN-TN' },
        { name: 'geo.placename', content: 'Erode, Tamil Nadu, India' },
        {
          name: 'keywords',
          content:
            'React SchemaForm, schema based forms, visual form builder, Zod metadata, FieldRenderer, dynamic forms, form layout builder, colSpan rowSpan form grid, TypeScript forms, Suriyaprakash Somu',
        },
        {
          name: 'ai-summary',
          content:
            'This page demonstrates a visual SchemaForm builder that creates schema metadata, configures field layout, and previews the output using a reusable React FieldRenderer.',
        },
        { property: 'og:title', content: title },
        { property: 'og:image:alt', content: title },
        { name: 'twitter:image:alt', content: title },
        { property: 'og:description', content: description },
        { property: 'og:locale', content: 'en_IN' },
        { property: 'og:type', content: 'website' },
        {
          property: 'og:image',
          content: 'https://www.suriyaprakash.in/og/playground-schemaform.png',
        },
        { property: 'og:url', content: url },
        { property: 'og:site_name', content: 'Suriyaprakash Somu' },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: title },
        { name: 'twitter:description', content: description },
        {
          name: 'twitter:image',
          content: 'https://www.suriyaprakash.in/og/playground-schemaform.png',
        },
      ],
      links: [
        { rel: 'canonical', href: url },
        {
          rel: 'alternate',
          type: 'application/rss+xml',
          title: 'RSS',
          href: '/rss.xml',
        },
      ],
      scripts: [
        {
          type: 'application/ld+json',
          children: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'SoftwareApplication',
            name: 'React SchemaForm Builder',
            applicationCategory: 'DeveloperApplication',
            operatingSystem: 'Web',
            url,
            description,
            creator: {
              '@type': 'Person',
              '@id': 'https://www.suriyaprakash.in/#person',
              url: 'https://www.suriyaprakash.in/',
              name: 'Suriyaprakash Somu',
              address: {
                '@type': 'PostalAddress',
                addressLocality: 'Erode',
                addressRegion: 'Tamil Nadu',
                addressCountry: 'IN',
              },
            },
            featureList: [
              'Visual field palette',
              'Live FieldRenderer preview',
              'Zod schema metadata output',
              'Section and group layout configuration',
              'colSpan and rowSpan grid controls',
              'Multi-select, password, switch, checkbox and select field previews',
            ],
            keywords: ['TypeScript', 'React', 'Zod'],
          }),
        },
      ],
    }
  },
})

function SchemaFormPage() {
  const demoRef = useRef<HTMLDivElement>(null)
  const [showDemo, setShowDemo] = useState(false)

  useEffect(() => {
    const element = demoRef.current
    if (!element) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.intersectionRatio >= 0.2) {
          setShowDemo(true)
          observer.disconnect()
        }
      },
      { threshold: 0.2 },
    )
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  return (
    <div className="min-h-screen bg-cream-50">
      <DemoPageHero
        system="SYS.02"
        eyebrow="React SchemaForm · Zod metadata"
        title="React form builder."
        accent="Shared definitions."
        description={
          <>
            A reusable foundation for data entry across admin and business
            applications. Add fields, arrange sections and preview the form with{' '}
            <code className="text-sage-200">FieldRenderer</code>.
          </>
        }
        specs={[
          { label: 'Field types', value: '11 controls' },
          { label: 'Layout', value: '12-column grid' },
          { label: 'Validation', value: 'Zod rules' },
          { label: 'Output', value: 'TSX + preview' },
        ]}
      />
      <DemoStage
        system="SYS.02"
        hint="Add fields from the palette, click any field to edit its metadata, and watch the form preview update as you change layout and validation."
      >
        <div ref={demoRef} className="min-h-[32rem]">
          {showDemo ? (
            <Suspense fallback={<SchemaBuilderPlaceholder />}>
              <SchemaBuilder />
            </Suspense>
          ) : (
            <SchemaBuilderPlaceholder />
          )}
        </div>
      </DemoStage>

      <section className="bg-cream-50 py-20 text-ink sm:py-28">
        <div className="container-custom">
          <SectionHeader
            eyebrow="What + how"
            title="Metadata in."
            accent="Interface out."
            description="A typed form system keeps fields, layout, validation, and rendered output on the same contract."
          />
          <div className="mt-14 grid gap-12 border-t border-ink/15 pt-10 lg:grid-cols-2 lg:gap-20 sm:mt-16 sm:pt-12">
            <Reveal>
              <article>
                <p className="preview-mono text-[10px] font-bold uppercase tracking-[0.22em] text-forest">
                  01 / What
                </p>
                <h2 className="mt-5 font-heading text-3xl tracking-[-0.02em] text-ink sm:text-4xl">
                  What is a schema-based form?
                </h2>
                <div className="mt-6 space-y-4 text-base leading-7 text-ink-soft sm:leading-8">
                  <p>
                    A schema-based form is a form whose fields, layout, and
                    validation rules are declared as data instead of
                    hand-written JSX. Instead of hard-coding every
                    <code className="rounded bg-cream-100 px-1.5 py-0.5 text-sm font-semibold text-ink">
                      {' '}
                      input{' '}
                    </code>
                    and button, you describe the form in a typed metadata object
                    — field type, label, validation, section, grid span — and a
                    generic renderer turns that metadata into the actual UI.
                  </p>
                  <p>
                    This playground is a working demonstration of that pattern.
                    Use the palette on the left to add fields, arrange them into
                    sections and groups, tune{' '}
                    <code className="rounded bg-cream-100 px-1.5 py-0.5 text-sm font-semibold text-ink">
                      colSpan
                    </code>{' '}
                    and{' '}
                    <code className="rounded bg-cream-100 px-1.5 py-0.5 text-sm font-semibold text-ink">
                      rowSpan
                    </code>
                    , then watch the reference{' '}
                    <strong className="font-semibold text-ink">
                      FieldRenderer
                    </strong>{' '}
                    build the live preview from that schema.
                  </p>
                </div>
              </article>
            </Reveal>

            <Reveal delay={100}>
              <article>
                <p className="preview-mono text-[10px] font-bold uppercase tracking-[0.22em] text-forest">
                  02 / How
                </p>
                <h2 className="mt-5 font-heading text-3xl tracking-[-0.02em] text-ink sm:text-4xl">
                  How it works
                </h2>
                <div className="mt-6 space-y-4 text-base leading-7 text-ink-soft sm:leading-8">
                  <p>
                    The builder produces a TypeScript-typed schema describing
                    every field and its layout. That same schema drives the live
                    preview and can be exported as generated{' '}
                    <strong className="font-semibold text-ink">
                      Zod schema code
                    </strong>{' '}
                    — keeping the field definitions, validation and rendered
                    form tied to a shared source. Integration still requires
                    server-side validation and application-specific rules.
                  </p>
                  <p>
                    The reusable{' '}
                    <code className="rounded bg-cream-100 px-1.5 py-0.5 text-sm font-semibold text-ink">
                      FieldRenderer
                    </code>{' '}
                    maps each field definition to its React input component,
                    applies the configured validation, and respects the section
                    and grid layout metadata. Add a field type once and every
                    form built with the system inherits it.
                  </p>
                </div>
              </article>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-ink py-20 text-cream-50 sm:py-28">
        <div className="container-custom">
          <SectionHeader
            tone="dark"
            eyebrow="Usage"
            title="One schema."
            accent="One source of truth."
            description="The generated Zod metadata can move directly from the visual builder into a typed React form."
          />
          <Reveal delay={100} className="mt-12 sm:mt-16">
            <DeferredSchemaFormUsage
              code={`import { z } from 'zod'
import { SchemaForm } from '@/components/form/SchemaForm'
import { fieldMeta } from '@/components/form/fieldMeta'

const schema = z.object({
  full_name: z.string().min(1).describe(fieldMeta({
    label: 'Full Name', placeholder: 'Enter your name', colSpan: 6
  })),
  email: z.string().email().describe(fieldMeta({
    label: 'Email', placeholder: 'you@example.com', colSpan: 6
  })),
  role: z.enum(['developer','designer','manager']).describe(fieldMeta({
    label: 'Role', colSpan: 6, section: 'Employment', inputType: 'select',
    options: [{ label: 'Developer', value: 'developer' }, { label: 'Designer', value: 'designer' }]
  })),
})

export default function EmployeeForm() {
  return (
    <SchemaForm
      schema={schema}
      onSubmit={(data) => console.log(data)}
      submitLabel="Submit"
    />
  )
}`}
            />
          </Reveal>
        </div>
      </section>

      <section className="bg-cream-50 py-20 text-ink sm:py-28">
        <div className="container-custom">
          <SectionHeader
            eyebrow="Fit"
            title="Built for repetition."
            accent="Not repetition by hand."
            description="Use the pattern where shared field contracts matter more than one-off form markup."
          />

          <div className="mt-14 border-b border-ink/15 sm:mt-16">
            <Reveal>
              <div className="grid gap-8 border-t border-ink/15 py-10 lg:grid-cols-[14rem_1fr] lg:gap-16 sm:py-12">
                <h2 className="font-heading text-3xl tracking-[-0.02em] sm:text-4xl">
                  Features
                </h2>
                <ol className="grid gap-x-10 sm:grid-cols-2">
                  {[
                    'Visual field palette with production field types',
                    'Live FieldRenderer preview as you configure',
                    'Sections, groups, colSpan and rowSpan layout control',
                    'Multi-select, password, switch, checkbox, select and text fields',
                    'Generated Zod schema code with TypeScript metadata',
                    'Schema-driven rendering — no hand-written form JSX',
                  ].map((feature, index) => (
                    <li
                      key={feature}
                      className="grid grid-cols-[2rem_1fr] gap-3 border-t border-ink/10 py-4 text-base leading-7 text-ink-soft first:border-t-0 sm:[&:nth-child(2)]:border-t-0"
                    >
                      <span className="preview-mono pt-0.5 text-[10px] font-bold text-forest">
                        0{index + 1}
                      </span>
                      {feature}
                    </li>
                  ))}
                </ol>
              </div>
            </Reveal>

            <Reveal delay={100}>
              <div className="grid gap-8 border-t border-ink/15 py-10 lg:grid-cols-[14rem_1fr] lg:gap-16 sm:py-12">
                <h2 className="font-heading text-3xl tracking-[-0.02em] sm:text-4xl">
                  When to use schema-driven forms
                </h2>
                <div className="space-y-5 text-base leading-7 text-ink-soft sm:leading-8">
                  <p>
                    This pattern pays off when an application ships many similar
                    forms — ERP modules, admin panels, CRM entry screens,
                    settings pages. Teams building role-based access control
                    systems also benefit: fields and sections can be filtered
                    per role using the same schema metadata that drives the UI.
                  </p>
                  <p>
                    Built with React, TypeScript and Zod. Related demo:{' '}
                    <Link
                      to="/playground/datatable"
                      className="font-semibold text-forest underline decoration-forest/30 underline-offset-2 hover:decoration-forest"
                    >
                      the React DataTable playground
                    </Link>
                    , which pairs these forms with sortable, paginated data
                    grids.
                  </p>
                  <p className="border-t border-ink/10 pt-5">
                    Continue through the system with the{' '}
                    <Link
                      to="/playground/querybuilder"
                      className="font-semibold text-forest underline decoration-forest/30 underline-offset-2 hover:decoration-forest"
                    >
                      React QueryBuilder playground
                    </Link>
                    , where Zod metadata becomes a typed filter interface.
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
      <ApplicationEnquiry />
    </div>
  )
}

function SchemaBuilderPlaceholder() {
  return (
    <div className="grid min-h-[32rem] place-items-center rounded-[1.25rem] border border-ink/15 bg-cream-50">
      <p className="preview-mono text-[10px] font-bold uppercase tracking-[0.18em] text-muted">
        Loading interactive builder…
      </p>
    </div>
  )
}
